// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import SiteSettings from "./SiteSettings/SiteSettings";
import ReportSettings from "./ReportSettings/ReportSettings";
import ExamPackageSettings from "./ExamPackageSettings/ExamPackageSettings";
import SimulationSettings from "./SimulationSettings/SimulationSettings";
import VideoSettings from "./VideoSettings/VideoSettings";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { ExamSliderProvider } from "src/contexts/exams/ExamSliderContext";
import { useClinicSettingsContext } from "src/contexts/clinic/ClinicSettingsContext";

// Utils
import { canUseAnySimulations } from "src/utils/clinic/clinicLicensingHelper";

const ClinicSettings = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText: sectionTitleColor } = getSurfaceTokens(theme);
  const { clinicInfo } = useClinicContext();
  const { currentSettings, handleUpdateSettings, isLoading, isUpdating } =
    useClinicSettingsContext();

  const canDisplayLensSim = useMemo(
    () => canUseAnySimulations(clinicInfo?.licensing),
    [clinicInfo?.licensing]
  );

  const canDisplayVideos = useMemo(
    () => clinicInfo?.licensing?.canPlayVideos,
    [clinicInfo?.licensing?.canPlayVideos]
  );

  return (
    <>
      <Grid
        container
        display="flex"
        flexDirection="column"
        width="100%"
        rowGap="16px"
      >
        <Typography
          sx={{ textTransform: "uppercase" }}
          fontSize="16px"
          color={sectionTitleColor}
        >
          {t("Site Settings")}
        </Typography>

        <SiteSettings
          isLoading={isLoading}
          isUpdating={isUpdating}
          currentSettings={currentSettings}
          handleUpdateSettings={handleUpdateSettings}
        />

        <ExamSliderProvider>
          <ExamPackageSettings
            isLoadingSettings={isLoading}
            isUpdatingSettings={isUpdating}
          />
        </ExamSliderProvider>

        <Typography
          sx={{ textTransform: "uppercase" }}
          fontSize="16px"
          color={sectionTitleColor}
        >
          {t("Report Settings")}
        </Typography>

        <ReportSettings
          isLoading={isLoading}
          isUpdating={isUpdating}
          currentSettings={currentSettings}
          handleUpdateSettings={handleUpdateSettings}
        />

        {canDisplayLensSim && (
          <SimulationSettings
            isLoadingSettings={isLoading}
            isUpdatingSettings={isUpdating}
            currentSettings={currentSettings}
            handleUpdateSettings={handleUpdateSettings}
          />
        )}
        {canDisplayVideos && (
          <VideoSettings
            isLoadingSettings={isLoading}
            isUpdatingSettings={isUpdating}
          />
        )}
      </Grid>
    </>
  );
};

export default ClinicSettings;
